import React, { useState, useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Badge,
  Collapse,
} from '@mui/material';
import { Link } from 'react-router-dom';
import {
  MdOutlineAttachFile,
  MdOutlineExpandMore,
  MdOutlineExpandLess,
  MdOutlineLogout,
} from 'react-icons/md';
import './Sidebar.scss';
import { IoPersonOutline, IoNotificationsOutline } from "react-icons/io5";
import { AiOutlineHome } from "react-icons/ai";
import { LuFilePenLine } from "react-icons/lu";
import { LiaClipboardListSolid } from "react-icons/lia";
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux'; // Import useDispatch
import { logout } from '../../../redux/slices/loginSlice';
import { fetchNotificationCount } from '../../../redux/slices/myAccount/notificationsSlice';

const Sidebar = ({ t, language }) => {
  const [selectedLink, setSelectedLink] = useState('profile');
  const [openUnits, setOpenUnits] = useState(false);
  const { isLoggedIn, user, token } = useSelector(state => state.login);
  const { count } = useSelector(
    (state) => state.notifications
  );

  const location = useLocation();
  const dispatch = useDispatch(); // Initialize dispatch hook
  const navigate = useNavigate();
  useEffect(() => {
    dispatch(fetchNotificationCount({ token, language }));
  }, [dispatch]);
  const handleLinkClick = (link) => {
    setSelectedLink(link);
  };
  useEffect(() => {
    // If no specific route is selected, navigate to the first tab
    if (location.pathname === '/my-account') {
      navigate('/my-account/profile'); // Default route
      setSelectedLink('profile');
    }
  }, [location, navigate]);
  const toggleUnits = () => {
    setOpenUnits(!openUnits);
  };

  const notificationCount = count || "";
  const sidebarItems = [
    {
      label: t.profile,
      icon: <IoPersonOutline className="list-item-icon" />,
      link: '/my-account/profile',
      id: 'profile',
    },
    {
      label: t.notifications,
      icon: <IoNotificationsOutline className="list-item-icon" />,
      link: '/my-account/notifications',
      id: 'notifications',
      badgeContent: notificationCount,
    },
    {
      label: t.contracts,
      icon: <LuFilePenLine className="list-item-icon" />,
      link: '/my-account/contracts',
      id: 'contracts',
    },
    {
      label: t.units,
      icon: <AiOutlineHome className="list-item-icon" />,
      link: '/my-account/units',
      id: 'units',
      collapsible: true,
      toggleFunction: toggleUnits, // Use toggleUnits function to open/close
      subItems: [
        { label: t.addNewUnit, link: '/my-account/add-unit', id: 'add-unit' },
        // { label: 'Units List', link: '/my-account/unit-list', id: 'unit-list' },
      ],
    },
    {
      label: t.attachments,
      icon: <MdOutlineAttachFile className="list-item-icon" />,
      link: '/my-account/attachments',
      id: 'attachments',
    },
    {
      label: t.orders,
      icon: <LiaClipboardListSolid className="list-item-icon" />,
      link: '/my-account/orders',
      id: 'orders',
    },
    {
      label: t.logout,
      icon: <MdOutlineLogout className="logout-icon" />,
      link: '#',
      id: 'logout',
      action: () => {
        dispatch(logout({ token }));
        navigate("/");
      },
      className: 'logout-item'
    },
  ];
  // Check if the link matches the current route
  const isLinkActive = (link) => {
    // If the link is '/my-account/orders', check if the current path matches or starts with '/my-account/orders/'
    if (link.startsWith('/my-account/orders')) {
      return location.pathname.startsWith('/my-account/orders');
    }
    if (link.startsWith('/my-account/contracts')) {
      return location.pathname.startsWith('/my-account/contracts');
    }
    return location.pathname === link;
  };

  const isSubItemActive = (subItems) => {
    return subItems?.some((subItem) => location.pathname === subItem.link);
  };
  return (
    <Box className="sidebar" sx={{ width: { md: "320px", xs: "280px" }, }}>
      <List>
        {sidebarItems.map((item) => (
          <React.Fragment key={item.id}>
            <ListItem
              button
              component={Link}
              to={item.link}
              onClick={() => {
                handleLinkClick(item.id);
                if (item.toggleFunction) item.toggleFunction(); // Call toggle function for collapsible items
                if (item.action) item.action(); // Call logout action when logout item is clicked
              }}
              className={`list-item ${isLinkActive(item.link) ? 'selected' : ''} ${item.id === 'logout' ? 'logout-item' : ''} ${item.collapsible && isSubItemActive(item.subItems) ? 'selected' : ''}`}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} className='list-label' />
              {item.badgeContent && (
                <Badge
                  badgeContent={item.badgeContent}
                  className={`badge ${item.badgeContent === 5 ? 'badge-highlight' : ''}`}
                />
              )}
              {item.collapsible && (openUnits ? (
                <MdOutlineExpandMore className="list-item-icon" />
              ) : (
                <MdOutlineExpandLess className="list-item-icon" />
              ))}
            </ListItem>

            {item.collapsible && (
              <Collapse in={openUnits} timeout="auto" unmountOnExit>
                <List component="div" disablePadding className="collapse-list">
                  {item.subItems.map((subItem) => (
                    <ListItem
                      button
                      component={Link}
                      to={subItem.link}
                      onClick={() => handleLinkClick(subItem.id)}
                      key={subItem.id}
                      className={`sub-item ${isLinkActive(subItem.link) ? 'selected' : ''}`}
                    >
                      <ListItemText primary={subItem.label} className='list-label' />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}

          </React.Fragment>
        ))}
      </List>
    </Box>
  );
};

export default Sidebar;
